import React, { useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Grid, Divider, Checkbox, FormControlLabel } from '@mui/material';
import DocumentsUploadCardContainer from './DocumentsUploadCardContainer';
import DocumentUploadTransitionComponent from './DocumentUploadTransitionComponent';
import { Add, Search, ErrorOutlined, CloudDownloadOutlined, LibraryAddOutlined, IndeterminateCheckBoxOutlined } from '@mui/icons-material';
import { RightsContext } from 'providers/RightsContext';
import { Autocomplete, TextField, Typography, Button, IconButton } from '@totalsoft/rocket-ui';
import { BulkActionsContainer, BulkActionsTriangle, EntityContainer, EntityTitle, WarningTypography } from './DocumentsStyles';

const DocumentsUploadListComponent = React.memo(
  ({
    t,
    documents,
    entities,
    documentId,
    fullDocumentsList,
    siteId,
    onAdd,
    onDelete,
    onDeleteFiles,
    changeLocalDocuments,
    filters,
    onFilterChanged,
    resetFilters,
    states,
    users,
    onToggleBulkModal,
    onSelectAllDocuments,
    selectAll,
    onSelectDocument,
    onBulkTransitionStateChangeClick,
    selectedDocumentsCount,
    downloadClick,
    actionsStates,
    setActionsStates,
    filesUploadInProgress,
    setFilesUploadInProgress
  }) => {
    const { uploadDeleteDocuments, readOnlyFull, uploadDocumentWrite, exportLatestDocumentVersions, exportAllDocumentVersions } =
      useContext(RightsContext);

    const availableTransitions = useMemo(() => {
      if (documents.length === 0) return [];
      const firstSelectedDocument = documents.find(document => document.document.isSelected);
      if (!firstSelectedDocument) return [];
      const firstDocumentTransitions = firstSelectedDocument.document.availableTransitions;
      const commonTransitions = firstDocumentTransitions.filter(transition => {
        return documents.every(document => {
          if (document.document.isSelected) {
            const documentTransitions = document.document.availableTransitions;
            return documentTransitions.some(
              documentTransition =>
                documentTransition.initialStateId === document.document.stateId &&
                transition.initialStateId === documentTransition.initialStateId &&
                transition.finalStateId === documentTransition.finalStateId
            );
          }
          return true;
        });
      });
      return commonTransitions;
    }, [documents]);

    const getBulkStatusWarning = useCallback(
      documents => {
        if (documents?.length === 0) return null;

        const selectedDocuments = documents.filter(doc => doc.document.isSelected);

        if (selectedDocuments.length === 0) return null;

        const firstSelectedDocument = selectedDocuments[0].document;

        const areAllInSameState = selectedDocuments.every(document => document.document.stateId === firstSelectedDocument.stateId);

        if (areAllInSameState) {
          const existsTransition = firstSelectedDocument.availableTransitions.some(firstTransition =>
            selectedDocuments.every(document => {
              const currentDocument = document.document;
              return (
                currentDocument.stateId === firstTransition.initialStateId &&
                currentDocument.availableTransitions.some(transition => transition.initialStateId === firstTransition.initialStateId)
              );
            })
          );
          if (!existsTransition) return t('OfferEdit.Documents.StatusChangeRestriction');
        }

        const hasCommonFinalState = firstSelectedDocument.availableTransitions.some(firstTransition =>
          selectedDocuments.every(document => {
            const currentDocument = document.document;
            return (
              currentDocument.stateId === firstTransition.initialStateId &&
              currentDocument.availableTransitions.some(
                transition =>
                  transition.finalStateId === firstTransition.finalStateId && transition.initialStateId === firstTransition.initialStateId
              )
            );
          })
        );
        if (!hasCommonFinalState) return t('OfferEdit.Documents.DifferentStatus');
        return null;
      },
      [t]
    );

    const fileIds = useMemo(
      () => fullDocumentsList.filter(document => document?.document?.isSelected).map(document => document.document.fileId),
      [fullDocumentsList]
    );

    const checkDocumentsDownloadConditions = useCallback(documents => {
      const selectedDocuments = documents.filter(doc => doc.document.isSelected);
      if (selectedDocuments.length === 0) return false;

      return !selectedDocuments.some(doc => !doc.document.isUploaded || !doc.document.canDownload);
    }, []);

    const handleCheck = useCallback(() => {
      switch (filters.mandatory) {
        case true:
          onFilterChanged('mandatory', false);
          break;
        case false:
          onFilterChanged('mandatory', null);
          break;
        default:
          onFilterChanged('mandatory', true);
          break;
      }
    }, [filters.mandatory, onFilterChanged]);

    const handleFilterChange = prop => event => onFilterChanged(prop, event);

    return (
      <Grid container>
        <Grid item xs={12} mb={4}>
          <Grid container spacing={2} alignItems='center' justifyContent='space-between'>
            <Grid item xs={12} xl>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6} lg xl={4} mt={2}>
                  <TextField
                    id={'documentsUploadContainer_filters_fullText'}
                    fullWidth={true}
                    autoComplete={'off'}
                    name='fullTextFilter'
                    value={filters.fullTextFilter || ''}
                    onChange={handleFilterChange('fullTextFilter')}
                    placeholder={t('OfferEdit.Documents.Search')}
                    startAdornment={<Search color='primary' sx={{ mr: 1 }} />}
                    endAdornment={
                      <IconButton
                        id={'documentsUploadContainer_filters_resetBtn'}
                        type='cancel'
                        variant='text'
                        size='tiny'
                        onClick={resetFilters}
                        tooltip={t('Tooltips.ResetFilters')}
                      />
                    }
                  />
                </Grid>
                {states && (
                  <Grid item xs={12} md={6} lg={3}>
                    <Autocomplete
                      label={t('OfferEdit.Documents.Status')}
                      id='docAttachment_status'
                      simpleValue={true}
                      isClearable={true}
                      isSearchable={true}
                      valueKey='stateName'
                      labelKey='stateName'
                      value={filters.stateName || ''}
                      onChange={handleFilterChange('stateName')}
                      options={states}
                    />
                  </Grid>
                )}
                {users && (
                  <Grid item xs={12} md={6} lg={3}>
                    <Autocomplete
                      label={t('OfferEdit.Documents.UploadedBy')}
                      simpleValue={true}
                      isClearable={true}
                      isSearchable={true}
                      id='docAttachment_user'
                      valueKey='userName'
                      labelKey='userName'
                      value={filters.userName || ''}
                      onChange={handleFilterChange('userName')}
                      options={users}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={6} lg='auto' xl={2} mt={1.5}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size='small'
                        onChange={handleCheck}
                        checked={filters.mandatory}
                        indeterminate={filters.mandatory === null}
                        indeterminateIcon={<IndeterminateCheckBoxOutlined />}
                      />
                    }
                    label={t('OfferEdit.Documents.Mandatory')}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs='auto'>
              <Grid container spacing={2} alignItems='center'>
                <Grid item xs='auto'>
                  <Button
                    id={'documentsUploadList_addBtn'}
                    color='primary'
                    variant='outlined'
                    startIcon={<Add />}
                    onClick={onAdd}
                    disabled={!uploadDeleteDocuments || readOnlyFull || !uploadDocumentWrite}
                  >
                    {t('OfferEdit.Documents.AddButton')}
                  </Button>
                </Grid>
                <Grid item xs='auto'>
                  <Button
                    id={'documentsUploadList_addBulk'}
                    color='primary'
                    variant='contained'
                    startIcon={<LibraryAddOutlined />}
                    onClick={onToggleBulkModal}
                    disabled={!uploadDeleteDocuments || readOnlyFull || !uploadDocumentWrite}
                  >
                    {t('BulkPdf.AddBulk')}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <BulkActionsContainer container alignItems='center' px={2.5} py={0.75} minHeight={7.5}>
            <Grid item xs='auto' mr={3.5}>
              <Checkbox onChange={onSelectAllDocuments} checked={selectAll} />
            </Grid>
            <Grid item xs>
              <Grid container spacing={2} display='flex' alignItems='center'>
                <Grid item>
                  <Typography>
                    {fullDocumentsList.some(doc => doc.document.isSelected)
                      ? t('OfferEdit.Documents.ChangeStatusTo')
                      : t('OfferEdit.Documents.SelectAll')}{' '}
                    ({selectedDocumentsCount} {t('OfferEdit.Documents.Selected').toLowerCase()})
                  </Typography>
                </Grid>
                <Grid item>
                  {fullDocumentsList.length > 0 && getBulkStatusWarning(fullDocumentsList) === null ? (
                    <DocumentUploadTransitionComponent
                      saving={actionsStates.saving}
                      documents={fullDocumentsList}
                      availableTransitions={availableTransitions}
                      onBulkTransitionStateChangeClick={onBulkTransitionStateChangeClick}
                    />
                  ) : (
                    fullDocumentsList.some(doc => doc.document.isSelected) && (
                      <Grid container spacing={1} display='flex' alignItems='center'>
                        <Grid item xs='auto' py='auto' display='flex' alignItems='center'>
                          <ErrorOutlined color='warning' />
                        </Grid>
                        <Grid item xs>
                          <WarningTypography emphasis='bold'>{getBulkStatusWarning(fullDocumentsList)}</WarningTypography>
                        </Grid>
                      </Grid>
                    )
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid item container xs='auto'>
              {exportLatestDocumentVersions && (
                <Grid item xs={12} lg ml={2} container justifyContent='right'>
                  <IconButton
                    variant='text'
                    tooltip={t('Tooltips.DownloadLatestVersions')}
                    onClick={() => downloadClick(fileIds, false)}
                    disabled={!checkDocumentsDownloadConditions(fullDocumentsList) || actionsStates.saving}
                  >
                    <CloudDownloadOutlined />
                  </IconButton>
                </Grid>
              )}
              {exportAllDocumentVersions && (
                <Grid item xs={12} lg ml={2} container justifyContent='right'>
                  <IconButton
                    variant='text'
                    tooltip={t('Tooltips.DownloadAllVersions')}
                    onClick={() => downloadClick(fileIds, true)}
                    disabled={!checkDocumentsDownloadConditions(fullDocumentsList) || actionsStates.saving}
                  >
                    <CloudDownloadOutlined />
                  </IconButton>
                </Grid>
              )}
            </Grid>
          </BulkActionsContainer>
        </Grid>
        <BulkActionsTriangle />
        <Grid container>
          {documents &&
            entities &&
            entities.map(entity => {
              return (
                <Grid item xs={12} key={entity}>
                  <EntityContainer container>
                    {documents.find(d => d.document.entityLabel === entity) && (
                      <EntityTitle item xs={12}>
                        <Typography>{entity}</Typography>
                        <Divider variant='fullWidth' />
                      </EntityTitle>
                    )}
                    {documents
                      .filter(d => d.document.entityLabel === entity)
                      .map(({ document, validationInfo, dirtyInfo }, idx) => {
                        return (
                          <Grid item xs={12} key={idx}>
                            <DocumentsUploadCardContainer
                              document={document}
                              documentId={documentId}
                              siteId={siteId}
                              onDelete={onDelete}
                              onDeleteFiles={onDeleteFiles}
                              changeLocalDocuments={changeLocalDocuments}
                              validationInfo={validationInfo || {}}
                              dirtyInfo={dirtyInfo || {}}
                              readOnlyFull={readOnlyFull || actionsStates.readOnlyAll}
                              uploadDocumentWrite={uploadDocumentWrite}
                              onSelectDocument={onSelectDocument}
                              actionsStates={actionsStates}
                              setActionsStates={setActionsStates}
                              filesUploadInProgress={filesUploadInProgress}
                              setFilesUploadInProgress={setFilesUploadInProgress}
                            />
                          </Grid>
                        );
                      })}
                  </EntityContainer>
                </Grid>
              );
            })}
        </Grid>
      </Grid>
    );
  }
);

DocumentsUploadListComponent.displayName = 'DocumentsUploadListComponent';

DocumentsUploadListComponent.propTypes = {
  t: PropTypes.func.isRequired,
  documents: PropTypes.array,
  fullDocumentsList: PropTypes.array.isRequired,
  entities: PropTypes.array,
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  onAdd: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDeleteFiles: PropTypes.func.isRequired,
  changeLocalDocuments: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired,
  onFilterChanged: PropTypes.func.isRequired,
  resetFilters: PropTypes.func.isRequired,
  states: PropTypes.array.isRequired,
  users: PropTypes.array.isRequired,
  onToggleBulkModal: PropTypes.func,
  onSelectAllDocuments: PropTypes.func.isRequired,
  selectAll: PropTypes.bool.isRequired,
  onSelectDocument: PropTypes.func.isRequired,
  onBulkTransitionStateChangeClick: PropTypes.func.isRequired,
  selectedDocumentsCount: PropTypes.number.isRequired,
  downloadClick: PropTypes.func.isRequired,
  actionsStates: PropTypes.object.isRequired,
  setActionsStates: PropTypes.func.isRequired,
  filesUploadInProgress: PropTypes.array.isRequired,
  setFilesUploadInProgress: PropTypes.func.isRequired
};

export default withTranslation()(DocumentsUploadListComponent);
